import { useCallback, useState } from 'react';
import { toast } from '@/hooks/useToast';
import { useI18n } from '@/hooks/useI18n';
import {
    buildClientsListWorkbook,
    clientsListFilename,
} from '@/lib/dashboard/clients/clients-list.export';
import type {
    AgencyClientRow,
    AgencyDashboardPeriod,
} from '@/integrations/supabase/rpc-types';

interface ExportClientsListArgs {
    rows: AgencyClientRow[];
    period: AgencyDashboardPeriod;
    search?: string;
    advisorName?: string | null;
}

interface UseClientsListExportResult {
    /** Vero mentre exceljs scende e il workbook si costruisce: il bottone resta disabilitato. */
    exporting: boolean;
    exportClients: (args: ExportClientsListArgs) => Promise<void>;
}

/**
 * Scarica la lista clienti come XLSX.
 *
 * Come il dossier del singolo cliente, non rilegge nulla dal database: esporta le righe che
 * la tab ha gia' in mano. Una seconda fetch potrebbe tornare con numeri diversi -- un
 * incasso registrato nel frattempo -- e il file smentirebbe lo schermo senza che nessuno
 * capisca perche'.
 */
export function useClientsListExport(): UseClientsListExportResult {
    const [exporting, setExporting] = useState(false);
    const { t, language } = useI18n();

    const exportClients = useCallback(
        async ({ rows, period, search, advisorName }: ExportClientsListArgs) => {
            setExporting(true);
            try {
                const blob = await buildClientsListWorkbook(rows, {
                    t,
                    period,
                    search,
                    advisorName,
                    locale: language === 'en' ? 'en-GB' : 'it-IT',
                });

                const filename = clientsListFilename(
                    t('dashboard.overview.clients.exportList.filenamePrefix')
                );

                const url = URL.createObjectURL(blob);
                const anchor = document.createElement('a');
                anchor.href = url;
                anchor.download = filename;
                document.body.appendChild(anchor);
                anchor.click();
                anchor.remove();
                // Rilasciato al giro dopo: revocarlo subito annulla il download su Safari,
                // che legge l'URL dopo il click e non durante.
                setTimeout(() => URL.revokeObjectURL(url), 0);

                toast({
                    title: t('dashboard.overview.clients.exportList.successTitle'),
                    description: filename,
                });
            } catch (error) {
                console.error('Clients list export failed:', error);
                toast({
                    title: t('common.error'),
                    description: t('dashboard.overview.clients.export.errorBody'),
                    variant: 'destructive',
                });
            } finally {
                setExporting(false);
            }
        },
        [t, language]
    );

    return { exporting, exportClients };
}

export default useClientsListExport;
